import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Dialog, DialogTitle, DialogContent,
  CircularProgress, IconButton, Menu, MenuItem, Button,
  FormControl, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Tooltip,
  Chip, Tabs, Tab
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Tooltip as ChartTooltip, Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, Legend);

const SECTOR_COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
  '#8BC34A', '#FF5722', '#00BCD4', '#E91E63',
];

/** Build sector structure with volume computation (exponential Archimedean spiral model). */
function buildEmptySectors(cfg) {
  const R = cfg.radius;
  const numS = cfg.num_sectors || 6;
  const numQ = cfg.num_quadrants || 4;
  const totalVol = cfg.total_volume_uL || 50;
  const r0 = R * 0.08;
  const usable = R - r0;
  const k = 4 * Math.log(10) / usable;

  const ringVolumes = [];
  const nSteps = 100;
  for (let ring = 0; ring < numS; ring++) {
    const rInner = r0 + usable * ring / numS;
    const rOuter = r0 + usable * (ring + 1) / numS;
    const dr = (rOuter - rInner) / nSteps;
    let vol = 0;
    for (let i = 0; i < nSteps; i++) {
      const r1 = rInner + i * dr;
      const r2 = rInner + (i + 1) * dr;
      const c1 = Math.exp(-k * (r1 - r0)) * 2 * Math.PI * r1;
      const c2 = Math.exp(-k * (r2 - r0)) * 2 * Math.PI * r2;
      vol += (c1 + c2) / 2 * dr;
    }
    ringVolumes.push(vol);
  }
  const totalRaw = ringVolumes.reduce((a, b) => a + b, 0);
  const normalized = ringVolumes.map((v) => (v / totalRaw) * totalVol);

  const sectors = [];
  for (let q = 0; q < numQ; q++) {
    const qLabel = numQ === 4 ? ((q === 0 || q === 2) ? 'A' : 'B') : String.fromCharCode(65 + q);
    for (let ring = 0; ring < numS; ring++) {
      const sectorNum = numS - ring;
      sectors.push({
        quadrant: qLabel,
        quadrant_idx: q,
        sector: sectorNum,
        colony_count: 0,
        volume_uL: Math.round(normalized[ring] / numQ * 1e6) / 1e6,
        cfu_per_mL: null,
      });
    }
  }
  return sectors;
}

/**
 * SpiralAnalysisChart -- Dialog for spiral plate colony counting results.
 *
 * Props:
 * - isOpen, onClose
 * - spiralData: pre-loaded results object (for restore mode)
 * - onApiCall(frameIdx) => Promise<{plate_config, frames}>
 * - objectMeta
 * - initialFrameIdx, maxFrameIdx
 * - availableFrames?: number[]
 */
const SpiralAnalysisChart = ({
  isOpen,
  onClose,
  spiralData: preloadedData,
  onApiCall,
  objectMeta,
  initialFrameIdx = 0,
  maxFrameIdx = 0,
  availableFrames,
  plateConfigOverride,
  frameImage,
  autoDetectedColonies,
}) => {
  const framesList = useMemo(() => {
    if (Array.isArray(availableFrames) && availableFrames.length) {
      return [...new Set(availableFrames)].map(Number).filter(Number.isFinite).sort((a, b) => a - b);
    }
    return Array.from({ length: maxFrameIdx + 1 }, (_, i) => i);
  }, [availableFrames, maxFrameIdx]);

  const initialIndex = useMemo(() => {
    const idx = framesList.indexOf(initialFrameIdx);
    return idx >= 0 ? idx : 0;
  }, [framesList, initialFrameIdx]);

  const [cursorIdx, setCursorIdx] = useState(initialIndex);
  const [allData, setAllData] = useState(null); // full result: {plate_config, frames}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // 0=plate viz, 1=results table, 2=bar chart
  const [plateImage, setPlateImage] = useState(null); // HTMLImageElement for plate view
  const [showColonyDots, setShowColonyDots] = useState(true);
  const [editMode, setEditMode] = useState(null); // null | 'add' | 'remove'
  const [colonyEdits, setColonyEdits] = useState({}); // { [frameKey]: { added: [], removed: Set } }

  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const canvasScaleRef = useRef({ scaleX: 1, scaleY: 1 });
  const [zoom, setZoom] = useState(1);
  const undoStackRef = useRef([]);

  // Load data on open
  useEffect(() => {
    if (!isOpen) return;
    setCursorIdx(initialIndex);
    setColonyEdits({});
    undoStackRef.current = [];
    setZoom(1);

    if (autoDetectedColonies && plateConfigOverride) {
      // Auto-detected colonies: pre-populate as "added" edits so user can filter
      const sectors = buildEmptySectors(plateConfigOverride);
      const frameKey = String(initialFrameIdx);
      setAllData({
        plate_config: plateConfigOverride,
        frames: { [frameKey]: {
          colonies: [],
          sectors,
          summary: { total_colonies: 0 },
        }},
        detection_method: 'auto_detect',
      });
      // Pre-populate as edits so user can remove false positives
      setColonyEdits({ [frameKey]: { added: [...autoDetectedColonies], removed: new Set() } });
      setEditMode(autoDetectedColonies.length > 0 ? 'remove' : 'add');
      setLoading(false);
      setError(null);
    } else if (preloadedData) {
      setAllData(preloadedData);
    } else {
      void fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialIndex]);

  const fetchData = async () => {
    if (!onApiCall) return;
    setLoading(true);
    setError(null);
    try {
      const result = await onApiCall(framesList[initialIndex] ?? 0);
      if (result) {
        setAllData(result);
      } else {
        setError('No spiral analysis data returned.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load spiral analysis data');
    } finally {
      setLoading(false);
    }
  };

  const realFrame = framesList[cursorIdx] ?? 0;
  const frameData = useMemo(() => allData?.frames?.[String(realFrame)] || null, [allData, realFrame]);
  const plateConfig = useMemo(() => allData?.plate_config || {}, [allData]);
  const rawColonies = useMemo(() => frameData?.colonies || [], [frameData]);
  const rawSectors = useMemo(() => frameData?.sectors || [], [frameData]);
  const rawSummary = useMemo(() => frameData?.summary || {}, [frameData]);
  const imageUrls = useMemo(() => allData?.image_urls || {}, [allData]);
  const detectionMethod = useMemo(() => allData?.detection_method || 'unknown', [allData]);

  // Apply colony edits (adds/removes) to get effective colonies list
  const frameEditKey = String(realFrame);
  const editsForFrame = useMemo(
    () => colonyEdits[frameEditKey] || { added: [], removed: new Set() },
    [colonyEdits, frameEditKey],
  );

  const colonies = useMemo(() => {
    const kept = rawColonies.filter((c) => !editsForFrame.removed.has(c.obj_id));
    return [...kept, ...editsForFrame.added];
  }, [rawColonies, editsForFrame]);

  // Sector assignment helper for added colonies
  const assignSector = useCallback((cx, cy) => {
    if (!plateConfig.center_x || !plateConfig.radius) return null;
    const dx = cx - plateConfig.center_x;
    const dy = cy - plateConfig.center_y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > plateConfig.radius) return null;

    const numQ = plateConfig.num_quadrants || 4;
    const numS = plateConfig.num_sectors || 6;
    const angle = ((Math.atan2(-dy, dx) * 180) / Math.PI + 360) % 360;
    const qIdx = Math.floor(angle / (360 / numQ));

    const r0 = plateConfig.radius * 0.08;
    const rUsable = plateConfig.radius - r0;
    let sIdx;
    if (dist < r0) {
      sIdx = numS;
    } else {
      const normalized = (dist - r0) / rUsable;
      const ring = Math.min(Math.floor(normalized * numS), numS - 1);
      sIdx = numS - ring;
    }

    const qLabel = numQ === 4 ? ((qIdx === 0 || qIdx === 2) ? 'A' : 'B') : String.fromCharCode(65 + qIdx);
    return { quadrant_idx: qIdx, sector: sIdx, quadrant: qLabel, r: dist, theta: angle };
  }, [plateConfig]);

  const pushUndo = useCallback(() => {
    const current = colonyEdits[frameEditKey];
    if (current) {
      undoStackRef.current.push({
        key: frameEditKey,
        snapshot: { added: [...current.added], removed: new Set(current.removed) },
      });
    } else {
      undoStackRef.current.push({ key: frameEditKey, snapshot: null });
    }
    if (undoStackRef.current.length > 100) undoStackRef.current.shift();
  }, [colonyEdits, frameEditKey]);

  const handleUndo = useCallback(() => {
    if (!undoStackRef.current.length) return;
    const { key, snapshot } = undoStackRef.current.pop();
    setColonyEdits((prev) => {
      const updated = { ...prev };
      if (snapshot) {
        updated[key] = snapshot;
      } else {
        delete updated[key];
      }
      return updated;
    });
  }, []);

  // Recompute sectors and summary from effective colonies
  const sectors = useMemo(() => {
    if (!editsForFrame.added.length && !editsForFrame.removed.size) return rawSectors;
    // Rebuild counts per cell from effective colonies
    const numQ = plateConfig.num_quadrants || 4;
    const numS = plateConfig.num_sectors || 6;
    const cellCounts = {};
    for (let q = 0; q < numQ; q++) {
      for (let s = 1; s <= numS; s++) {
        cellCounts[`${q}-${s}`] = 0;
      }
    }
    colonies.forEach((c) => {
      if (c.quadrant !== 'outside' && c.quadrant_idx >= 0) {
        const key = `${c.quadrant_idx}-${c.sector}`;
        cellCounts[key] = (cellCounts[key] || 0) + 1;
      }
    });
    return rawSectors.map((s) => ({
      ...s,
      colony_count: cellCounts[`${s.quadrant_idx}-${s.sector}`] ?? s.colony_count,
      cfu_per_mL: s.volume_uL > 0
        ? Math.round((cellCounts[`${s.quadrant_idx}-${s.sector}`] ?? s.colony_count) / (s.volume_uL / 1000) * 10) / 10
        : null,
    }));
  }, [rawSectors, colonies, editsForFrame, plateConfig]);

  const summary = useMemo(() => {
    if (!editsForFrame.added.length && !editsForFrame.removed.size) return rawSummary;
    const total = colonies.filter((c) => c.quadrant !== 'outside').length;
    return { total_colonies: total };
  }, [colonies, editsForFrame, rawSummary]);

  // Load plate image when frame changes.
  // Priority: frameImage prop (ImageBitmap from useFrameManager) > presigned URL from backend.
  useEffect(() => {
    if (frameImage) {
      setPlateImage(frameImage);
      return;
    }
    const url = imageUrls[String(realFrame)];
    if (!url) {
      setPlateImage(null);
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => setPlateImage(img);
    img.onerror = () => setPlateImage(null);
    img.src = url;
  }, [imageUrls, realFrame, frameImage]);

  // Group sectors by quadrant label for display
  const sectorsByQuadrant = useMemo(() => {
    const map = {};
    sectors.forEach((s) => {
      const key = `${s.quadrant}${s.quadrant_idx}`;
      if (!map[key]) map[key] = { label: s.quadrant, quadrant_idx: s.quadrant_idx, sectors: [] };
      map[key].sectors.push(s);
    });
    return Object.values(map).sort((a, b) => a.quadrant_idx - b.quadrant_idx);
  }, [sectors]);

  // Aggregated sectors by sector number (across all quadrants)
  const aggregatedBySector = useMemo(() => {
    const agg = {};
    sectors.forEach((s) => {
      if (!agg[s.sector]) {
        agg[s.sector] = { sector: s.sector, totalCount: 0, totalVolume: 0 };
      }
      agg[s.sector].totalCount += s.colony_count;
      agg[s.sector].totalVolume += s.volume_uL;
    });
    return Object.values(agg).sort((a, b) => a.sector - b.sector);
  }, [sectors]);

  // ---- Canvas Plate Visualization ----
  const drawPlate = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !plateConfig.radius) return;

    const ctx = canvas.getContext('2d');
    const containerW = canvas.parentElement?.clientWidth || 700;
    const maxSize = Math.min(containerW, 700);

    const numSectors = plateConfig.num_sectors || 6;
    const numQuadrants = plateConfig.num_quadrants || 4;

    if (plateImage) {
      // -- Image-based plate view --
      // ImageBitmap has .width/.height; HTMLImageElement has .naturalWidth/.naturalHeight
      const imgW = plateImage.naturalWidth ?? plateImage.width;
      const imgH = plateImage.naturalHeight ?? plateImage.height;
      const aspect = imgW / imgH;

      let drawW, drawH;
      if (aspect > 1) {
        drawW = maxSize;
        drawH = maxSize / aspect;
      } else {
        drawH = maxSize;
        drawW = maxSize * aspect;
      }
      canvas.width = Math.round(drawW);
      canvas.height = Math.round(drawH);

      const scaleX = drawW / imgW;
      const scaleY = drawH / imgH;

      // Draw the actual dish image
      ctx.drawImage(plateImage, 0, 0, drawW, drawH);
      canvasScaleRef.current = { scaleX, scaleY };

      // Scale plate coordinates to display space
      const cx = plateConfig.center_x * scaleX;
      const cy = plateConfig.center_y * scaleY;
      const R = plateConfig.radius * scaleX; // assumes roughly square pixels
      const deadZone = R * 0.08;

      // Semi-transparent overlay outside plate
      ctx.save();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
      ctx.fillRect(0, 0, drawW, drawH);
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // Plate circle outline
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Concentric sector rings with labels
      for (let s = 1; s <= numSectors; s++) {
        if (s < numSectors) {
          const ringR = deadZone + (R - deadZone) * (s / numSectors);
          ctx.beginPath();
          ctx.arc(cx, cy, ringR, 0, 2 * Math.PI);
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
        // Sector label (S1=outer, S6=inner) along 0-degree axis
        const ringFromInner = numSectors - s;
        const rMid = deadZone + (R - deadZone) * ((ringFromInner + 0.5) / numSectors);
        ctx.fillStyle = 'rgba(255, 255, 255, 0.55)';
        ctx.font = '10px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`S${s}`, cx + rMid, cy - 6);
      }

      // Dead zone indicator
      ctx.beginPath();
      ctx.arc(cx, cy, deadZone, 0, 2 * Math.PI);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Quadrant divider lines
      for (let q = 0; q < numQuadrants; q++) {
        const angle = (2 * Math.PI * q) / numQuadrants;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + R * Math.cos(angle), cy - R * Math.sin(angle));
        ctx.strokeStyle = 'rgba(255, 220, 50, 0.6)';
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }

      // Colony masks at actual positions
      if (showColonyDots) {
        colonies.forEach((col) => {
          if (col.quadrant === 'outside') return;
          const px = col.centroid_x * scaleX;
          const py = col.centroid_y * scaleY;
          const isAdded = col._added;
          const sectorColor = isAdded ? '#00ff88' : SECTOR_COLORS[(col.sector - 1) % SECTOR_COLORS.length];
          // Size proportional to colony area (mask-like appearance)
          const areaScaled = (col.area_pixels || 25) * scaleX * scaleY;
          const colonyR = Math.max(4, Math.min(20, Math.sqrt(areaScaled / Math.PI)));

          // Filled semi-transparent mask
          ctx.beginPath();
          ctx.arc(px, py, colonyR, 0, 2 * Math.PI);
          ctx.fillStyle = sectorColor;
          ctx.globalAlpha = 0.4;
          ctx.fill();
          ctx.globalAlpha = 1.0;

          // Solid border
          ctx.beginPath();
          ctx.arc(px, py, colonyR, 0, 2 * Math.PI);
          ctx.strokeStyle = isAdded ? '#00cc66' : sectorColor;
          ctx.lineWidth = isAdded ? 2 : 1.5;
          ctx.stroke();

          // In remove mode, draw X on each colony
          if (editMode === 'remove') {
            const xSize = Math.max(colonyR, 6);
            ctx.beginPath();
            ctx.moveTo(px - xSize, py - xSize);
            ctx.lineTo(px + xSize, py + xSize);
            ctx.moveTo(px + xSize, py - xSize);
            ctx.lineTo(px - xSize, py + xSize);
            ctx.strokeStyle = 'rgba(255, 60, 60, 0.9)';
            ctx.lineWidth = 2;
            ctx.stroke();
          }
        });
      }

      // Quadrant labels
      for (let q = 0; q < numQuadrants; q++) {
        const midAngle = (2 * Math.PI * q) / numQuadrants + Math.PI / numQuadrants;
        const labelR = R * 0.92;
        const lx = cx + labelR * Math.cos(midAngle);
        const ly = cy - labelR * Math.sin(midAngle);
        const qLabel = numQuadrants === 4 ? ((q === 0 || q === 2) ? 'A' : 'B') : String.fromCharCode(65 + q);

        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.font = 'bold 14px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(qLabel, lx, ly);
      }

      // Title
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        `Frame ${realFrame} - ${summary.total_colonies || 0} colonies`,
        drawW / 2, drawH - 10,
      );
    } else {
      // -- Schematic fallback (no image available) --
      const padding = 40;
      const size = Math.min(maxSize, 600);
      canvas.width = size;
      canvas.height = size;

      const cx = size / 2;
      const cy = size / 2;
      const scale = (size - 2 * padding) / (2 * plateConfig.radius);
      const R = plateConfig.radius * scale;
      const deadZone = R * 0.08;

      ctx.clearRect(0, 0, size, size);

      // Plate background
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, 2 * Math.PI);
      ctx.fillStyle = '#f8f9fa';
      ctx.fill();
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Concentric sector rings
      for (let s = 1; s <= numSectors; s++) {
        const ringFromInner = numSectors - s;
        const rInner = deadZone + (R - deadZone) * (ringFromInner / numSectors);
        ctx.beginPath();
        ctx.arc(cx, cy, rInner, 0, 2 * Math.PI);
        ctx.strokeStyle = '#ccc';
        ctx.lineWidth = 1;
        ctx.stroke();

        const rMid = deadZone + (R - deadZone) * ((ringFromInner + 0.5) / numSectors);
        ctx.fillStyle = '#888';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`S${s}`, cx + rMid * 0.85, cy - 8);
      }

      // Dead zone
      ctx.beginPath();
      ctx.arc(cx, cy, deadZone, 0, 2 * Math.PI);
      ctx.fillStyle = 'rgba(200,200,200,0.3)';
      ctx.fill();
      ctx.strokeStyle = '#aaa';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Quadrant lines
      for (let q = 0; q < numQuadrants; q++) {
        const angle = (2 * Math.PI * q) / numQuadrants;
        ctx.beginPath();
        ctx.moveTo(cx, cy);
        ctx.lineTo(cx + R * Math.cos(angle), cy - R * Math.sin(angle));
        ctx.strokeStyle = '#999';
        ctx.lineWidth = 1;
        ctx.stroke();

        const midAngle = angle + Math.PI / numQuadrants;
        const labelR = R * 0.95;
        const lx = cx + labelR * Math.cos(midAngle);
        const ly = cy - labelR * Math.sin(midAngle);
        const qLabel = numQuadrants === 4 ? ((q === 0 || q === 2) ? 'A' : 'B') : String.fromCharCode(65 + q);
        ctx.fillStyle = '#555';
        ctx.font = 'bold 13px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(qLabel, lx, ly);
      }

      // Colony dots
      if (showColonyDots) {
        colonies.forEach((col) => {
        if (col.quadrant === 'outside') return;
        const dx = (col.centroid_x - plateConfig.center_x) * scale;
        const dy = (col.centroid_y - plateConfig.center_y) * scale;
        const px = cx + dx;
        const py = cy + dy;
        const sectorColor = SECTOR_COLORS[(col.sector - 1) % SECTOR_COLORS.length];
        ctx.beginPath();
        ctx.arc(px, py, 4, 0, 2 * Math.PI);
        ctx.fillStyle = sectorColor;
        ctx.fill();
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      }

      ctx.fillStyle = '#333';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(
        `Frame ${realFrame} - ${summary.total_colonies || 0} colonies`,
        cx, size - 10,
      );
    }
  }, [plateConfig, colonies, summary, realFrame, plateImage, showColonyDots, editMode]);

  useEffect(() => {
    if (activeTab === 0 && frameData) {
      // Small delay to ensure canvas is mounted
      const timer = setTimeout(drawPlate, 50);
      return () => clearTimeout(timer);
    }
  }, [activeTab, frameData, drawPlate]);

  // Keyboard shortcut: Ctrl/Cmd+Z for undo
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleUndo]);

  const handleCanvasWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.15 : 0.15;
    setZoom((prev) => Math.max(0.5, Math.min(5, +(prev + delta).toFixed(2))));
  }, []);

  // Canvas click handler for add/remove colony editing
  const handleCanvasClick = useCallback((e) => {
    if (!editMode || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    // Account for CSS scaling (canvas may be displayed smaller than its logical size)
    const cssScaleX = canvasRef.current.width / rect.width;
    const cssScaleY = canvasRef.current.height / rect.height;
    const displayX = (e.clientX - rect.left) * cssScaleX;
    const displayY = (e.clientY - rect.top) * cssScaleY;

    const { scaleX, scaleY } = canvasScaleRef.current;
    // Convert display coords to native image coords
    const nativeX = displayX / scaleX;
    const nativeY = displayY / scaleY;

    const key = frameEditKey;

    if (editMode === 'remove') {
      // Find closest colony within 20px display radius
      let closestId = null;
      let closestDist = Infinity;
      colonies.forEach((c) => {
        const cdx = c.centroid_x * scaleX - displayX;
        const cdy = c.centroid_y * scaleY - displayY;
        const d = Math.sqrt(cdx * cdx + cdy * cdy);
        if (d < closestDist && d < 20) {
          closestDist = d;
          closestId = c.obj_id;
        }
      });
      if (closestId != null) {
        pushUndo();
        setColonyEdits((prev) => {
          const existing = prev[key] || { added: [], removed: new Set() };
          const newRemoved = new Set(existing.removed);
          newRemoved.add(closestId);
          // If it's a manually added colony, just remove it from the added list
          const newAdded = existing.added.filter((c) => c.obj_id !== closestId);
          return { ...prev, [key]: { added: newAdded, removed: newRemoved } };
        });
      }
    } else if (editMode === 'add') {
      const assignment = assignSector(nativeX, nativeY);
      if (!assignment) return; // clicked outside plate
      const newId = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const newColony = {
        obj_id: newId,
        centroid_x: Math.round(nativeX * 10) / 10,
        centroid_y: Math.round(nativeY * 10) / 10,
        area_pixels: 25,
        r: Math.round(assignment.r * 10) / 10,
        theta: Math.round(assignment.theta * 10) / 10,
        quadrant: assignment.quadrant,
        quadrant_idx: assignment.quadrant_idx,
        sector: assignment.sector,
        _added: true,
      };
      pushUndo();
      setColonyEdits((prev) => {
        const existing = prev[key] || { added: [], removed: new Set() };
        return { ...prev, [key]: { ...existing, added: [...existing.added, newColony] } };
      });
    }
  }, [editMode, colonies, frameEditKey, assignSector, pushUndo]);

  // ---- Bar Chart Data ----
  const barChartData = useMemo(() => {
    if (!aggregatedBySector.length) return null;
    const labels = aggregatedBySector.map((a) => `Sector ${a.sector}`);
    return {
      labels,
      datasets: [
        {
          label: 'Colony Count',
          data: aggregatedBySector.map((a) => a.totalCount),
          backgroundColor: aggregatedBySector.map((a) => SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length] + '99'),
          borderColor: aggregatedBySector.map((a) => SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length]),
          borderWidth: 1,
        },
        {
          label: 'CFU/mL (x1000)',
          data: aggregatedBySector.map((a) => {
            const vol = a.totalVolume / 1000; // uL to mL
            return vol > 0 ? Math.round((a.totalCount / vol) / 1000) : 0;
          }),
          backgroundColor: 'rgba(75, 192, 192, 0.3)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [aggregatedBySector]);

  const barChartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            if (ctx.datasetIndex === 1) {
              return `CFU/mL: ${(ctx.parsed.y * 1000).toLocaleString()}`;
            }
            return `Colonies: ${ctx.parsed.y}`;
          },
        },
      },
    },
    scales: {
      x: { title: { display: true, text: 'Sector (1=outer, 6=inner)' } },
      y: { title: { display: true, text: 'Count / CFU(x1000)' }, beginAtZero: true },
    },
  }), []);

  // ---- Frame Navigation ----
  const handlePrev = () => {
    if (cursorIdx <= 0 || loading) return;
    setCursorIdx(cursorIdx - 1);
  };
  const handleNext = () => {
    if (cursorIdx >= framesList.length - 1 || loading) return;
    setCursorIdx(cursorIdx + 1);
  };
  const handleSelectChange = (e) => {
    const value = Number(e.target.value);
    const idx = framesList.indexOf(value);
    if (idx >= 0) setCursorIdx(idx);
  };

  // ---- Downloads ----
  const downloadSectorXlsx = () => {
    if (!sectors.length) return;
    const headers = ['Quadrant', 'Quadrant_Idx', 'Sector', 'Colony_Count', 'Volume_uL', 'CFU_per_mL'];
    const rows = sectors.map((s) => [
      s.quadrant, s.quadrant_idx, s.sector, s.colony_count,
      s.volume_uL, s.cfu_per_mL ?? '',
    ]);
    const summaryRows = [
      [],
      ['Summary'],
      ['Total Colonies', summary.total_colonies || 0],
    ];
    const wb = XLSX.utils.book_new();
    const sectorSheet = XLSX.utils.aoa_to_sheet([headers, ...rows, ...summaryRows]);
    XLSX.utils.book_append_sheet(wb, sectorSheet, 'Sector Data');

    // Colony details sheet
    if (colonies.length) {
      const colHeaders = ['Object_ID', 'Name', 'Centroid_X', 'Centroid_Y', 'Area_px', 'Distance_px', 'Angle_deg', 'Quadrant', 'Sector'];
      const colRows = colonies.map((c) => [
        c.obj_id,
        objectMeta?.[c.obj_id]?.name || `Colony ${c.obj_id}`,
        c.centroid_x, c.centroid_y, c.area_pixels || '',
        c.r, c.theta, c.quadrant, c.sector,
      ]);
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([colHeaders, ...colRows]), 'Colony Details');
    }

    XLSX.writeFile(wb, `spiral_analysis_frame_${realFrame}.xlsx`);
  };

  const downloadChartPng = () => {
    // For plate viz tab, export canvas; for bar chart tab, export chart
    if (activeTab === 0 && canvasRef.current) {
      const url = canvasRef.current.toDataURL('image/png', 1.0);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spiral_plate_frame_${realFrame}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else if (activeTab === 2 && chartRef.current?.toBase64Image) {
      const url = chartRef.current.toBase64Image('image/png', 1.0);
      const a = document.createElement('a');
      a.href = url;
      a.download = `spiral_chart_frame_${realFrame}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ style: { height: '90vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{autoDetectedColonies ? 'Spiral Plate - Colony Detection' : 'Spiral Plate Analysis'}</Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Colony counting per sector with CFU/mL estimation
            </Typography>
          </Box>
          <Box>
            <IconButton onClick={(e) => setDownloadMenuAnchor(e.currentTarget)} title="Download">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small" title="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
        {/* Frame navigation */}
        {!autoDetectedColonies && (
        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
          <IconButton onClick={handlePrev} disabled={cursorIdx <= 0 || loading} size="small">
            <NavigateBeforeIcon />
          </IconButton>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <TextField select label="Frame" value={realFrame} onChange={handleSelectChange} size="small">
              {framesList.map((f) => (
                <MenuItem key={f} value={f}>Frame {f}</MenuItem>
              ))}
            </TextField>
          </FormControl>
          <Typography variant="body2" sx={{ color: '#888' }}>
            {cursorIdx + 1} / {framesList.length}
          </Typography>
          <IconButton onClick={handleNext} disabled={cursorIdx >= framesList.length - 1 || loading} size="small">
            <NavigateNextIcon />
          </IconButton>
        </Box>
        )}

        {/* Content */}
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" flex={1} gap={2}>
            <CircularProgress size={48} />
            <Typography color="text.secondary">Analyzing spiral plate...</Typography>
          </Box>
        ) : error ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : frameData ? (
          <>
            {/* Summary bar */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Chip
                label={`Total: ${summary.total_colonies ?? 0} colonies`}
                color="primary"
                variant="outlined"
              />
            </Box>

            {/* Plate config info */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Plate: {plateConfig.plate_diameter_mm || 90}mm |
                Volume: {plateConfig.total_volume_uL || 50}uL |
                Grid: {plateConfig.num_quadrants || 4} quadrants x {plateConfig.num_sectors || 6} sectors
              </Typography>
              <Chip
                label={`Detection: ${detectionMethod === 'auto_detect' ? 'Auto-Detect' : detectionMethod === 'image_cv' ? 'Image CV' : detectionMethod}`}
                size="small"
                variant="outlined"
                color={detectionMethod === 'auto_detect' ? 'primary' : 'default'}
                sx={{ fontSize: '0.65rem', height: 20 }}
              />
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
            >
              <Tab label="Plate View" sx={{ minHeight: 36, textTransform: 'none' }} />
              <Tab label="Results Table" sx={{ minHeight: 36, textTransform: 'none' }} />
              <Tab label="Bar Chart" sx={{ minHeight: 36, textTransform: 'none' }} />
            </Tabs>

            {/* Tab 0: Plate Visualization */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 400 }}>
                {/* Plate view toolbar */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
                  <Tooltip title={showColonyDots ? 'Hide colony markers' : 'Show colony markers'} arrow>
                    <IconButton
                      size="small"
                      onClick={() => setShowColonyDots((v) => !v)}
                      sx={{ color: showColonyDots ? '#1976d2' : '#aaa' }}
                    >
                      {showColonyDots ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />
                  <Button
                    size="small"
                    variant={editMode === 'add' ? 'contained' : 'outlined'}
                    color="success"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => setEditMode(editMode === 'add' ? null : 'add')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Add Colony
                  </Button>
                  <Button
                    size="small"
                    variant={editMode === 'remove' ? 'contained' : 'outlined'}
                    color="error"
                    startIcon={<RemoveCircleOutlineIcon />}
                    onClick={() => setEditMode(editMode === 'remove' ? null : 'remove')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Remove Colony
                  </Button>
                  <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />
                  <Tooltip title="Undo last action (Ctrl+Z)" arrow>
                    <span>
                      <IconButton
                        size="small"
                        onClick={handleUndo}
                        disabled={!undoStackRef.current.length}
                        sx={{ color: '#666' }}
                      >
                        <UndoIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />
                  <Tooltip title="Zoom out" arrow>
                    <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))} sx={{ color: '#666' }}>
                      <ZoomOutIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Typography variant="caption" sx={{ color: '#888', minWidth: 36, textAlign: 'center' }}>
                    {Math.round(zoom * 100)}%
                  </Typography>
                  <Tooltip title="Zoom in" arrow>
                    <IconButton size="small" onClick={() => setZoom((z) => Math.min(5, +(z + 0.25).toFixed(2)))} sx={{ color: '#666' }}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {(editsForFrame.added.length > 0 || editsForFrame.removed.size > 0) && (
                    <>
                      <Box sx={{ borderLeft: '1px solid #ddd', height: 24, mx: 0.5 }} />
                      <Typography variant="caption" sx={{ color: '#888' }}>
                        +{editsForFrame.added.length} / -{editsForFrame.removed.size} edits
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => {
                          undoStackRef.current = [];
                          setColonyEdits((prev) => {
                            const updated = { ...prev };
                            delete updated[frameEditKey];
                            return updated;
                          });
                        }}
                        sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#888' }}
                      >
                        Reset
                      </Button>
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    display: 'flex', justifyContent: 'center', flex: 1,
                    bgcolor: plateImage ? '#111' : 'transparent', borderRadius: 1,
                    cursor: editMode === 'add' ? 'crosshair' : editMode === 'remove' ? 'pointer' : 'default',
                    overflow: 'auto',
                  }}
                  onWheel={handleCanvasWheel}
                >
                  <canvas
                    ref={canvasRef}
                    style={{
                      maxWidth: zoom <= 1 ? '100%' : 'none',
                      maxHeight: zoom <= 1 ? '100%' : 'none',
                      width: zoom > 1 ? `${Math.round(zoom * 100)}%` : undefined,
                    }}
                    onClick={handleCanvasClick}
                  />
                </Box>
              </Box>
            )}

            {/* Tab 1: Results Table */}
            {activeTab === 1 && (
              <>
                {/* Per-quadrant sector table */}
                {sectorsByQuadrant.map((qGroup) => (
                  <Box key={qGroup.quadrant_idx} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Quadrant {qGroup.label} (#{qGroup.quadrant_idx})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Colonies</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Volume (uL)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>CFU/mL</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {qGroup.sectors.sort((a, b) => a.sector - b.sector).map((s) => (
                            <TableRow
                              key={s.sector}
                              hover
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box sx={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    bgcolor: SECTOR_COLORS[(s.sector - 1) % SECTOR_COLORS.length],
                                  }} />
                                  Sector {s.sector}
                                  {s.sector === 1 && (
                                    <Typography variant="caption" color="text.secondary">(outer)</Typography>
                                  )}
                                  {s.sector === (plateConfig.num_sectors || 6) && (
                                    <Typography variant="caption" color="text.secondary">(inner)</Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {s.colony_count}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                  {s.volume_uL?.toFixed(3) ?? '-'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                  {s.cfu_per_mL != null ? Math.round(s.cfu_per_mL).toLocaleString() : '-'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}

                {/* Aggregated summary table */}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>
                  Aggregated (All Quadrants)
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Colonies</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Total Volume (uL)</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>CFU/mL</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aggregatedBySector.map((a) => {
                        const volML = a.totalVolume / 1000;
                        const cfu = volML > 0 ? Math.round(a.totalCount / volML) : null;
                        return (
                          <TableRow key={a.sector} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{
                                  width: 10, height: 10, borderRadius: '50%',
                                  bgcolor: SECTOR_COLORS[(a.sector - 1) % SECTOR_COLORS.length],
                                }} />
                                Sector {a.sector}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {a.totalCount}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                {a.totalVolume.toFixed(3)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                {cfu != null ? cfu.toLocaleString() : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Technique guide */}
                <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0', mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#444' }}>
                    Spiral Plating Reference
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
                    <Box>
                      <Chip label="Sector Numbering" size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }} />
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', lineHeight: 1.5 }}>
                        Sector 1 = outermost (most dilute), Sector 6 = innermost (most concentrated).
                      </Typography>
                    </Box>
                    <Box>
                      <Chip label="CFU/mL Formula" size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }} />
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', lineHeight: 1.5 }}>
                        CFU/mL = colony_count / volume_in_sector_mL. Volume is modeled using Archimedean spiral area-proportional deposition.
                      </Typography>
                    </Box>
                    <Box>
                      <Chip label="Quadrant Groups" size="small" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.7rem' }} />
                      <Typography variant="caption" sx={{ display: 'block', color: '#666', lineHeight: 1.5 }}>
                        Standard Interscience grid: 4 quadrants in 2 groups (A, B). Opposing quadrants share a label for statistical averaging.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}

            {/* Tab 2: Bar Chart */}
            {activeTab === 2 && (
              <Box sx={{ flex: 1, minHeight: 350 }}>
                {barChartData ? (
                  <Bar ref={chartRef} data={barChartData} options={barChartOptions} />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" flex={1} sx={{ minHeight: 200 }}>
                    <Typography color="text.secondary">No sector data available for chart.</Typography>
                  </Box>
                )}
              </Box>
            )}
          </>
        ) : !loading ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <Typography color="text.secondary">
              No data available for frame {realFrame}. Run spiral analysis first.
            </Typography>
          </Box>
        ) : null}
      </DialogContent>

      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem onClick={() => { downloadSectorXlsx(); setDownloadMenuAnchor(null); }}>
          Download Data (XLSX)
        </MenuItem>
        {(activeTab === 0 || activeTab === 2) && (
          <MenuItem onClick={() => { downloadChartPng(); setDownloadMenuAnchor(null); }}>
            Download Chart (PNG)
          </MenuItem>
        )}
      </Menu>
    </Dialog>
  );
};

export { SpiralAnalysisChart };
export default SpiralAnalysisChart;
